import { useState, useEffect } from "react";
import { Menu, ChevronRight, LogOut, TextSearch } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "./hooks/useSession";
import { useUser } from "./contexts/UserContext";
import { useMenuAccess } from "./contexts/MenuAccessContext";
import { useIconResolver } from "./contexts/IconResolverContext";
import { getColorStyle } from "./utils/tailwindColors";
import { normalizeFunctionPath } from "./utils/menuAccess";
import { FaSearch, FaPrint, FaQrcode, FaFileImport, FaStar, FaRegStar } from "react-icons/fa";
import { AiFillAlert } from "react-icons/ai";
import { IoDocumentTextOutline } from "react-icons/io5";
import { SlReload, SlNote } from "react-icons/sl";
import { MdAssignment, MdHome } from "react-icons/md";
import { LuSheet } from "react-icons/lu";
import { FcSalesPerformance } from "react-icons/fc";
import AnnouncementBell from "./components/AnnouncementBell";

const API_BASE_URL = "/api3000";

// 共通のスタイルコンポーネント
const MenuItem = ({ children, onClick, className = "" }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ${className}`}
  >
    {children}
  </button>
);

export default function Sidebar({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMyCustomerActive, setIsMyCustomerActive] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const navigate = useNavigate();
  const location = useLocation();
  const { extendSession, endSession } = useSession();
  const { user } = useUser();
  const { menuSections, menuLoadError } = useMenuAccess();
  const { getMenuIconComponent } = useIconResolver();

  // メニュー読み込み後、未設定のセクションは開いた状態にする（既存の開閉は維持）
  useEffect(() => {
    const sections = menuSections.filter((b) => b.type === "section");
    if (sections.length === 0) return;
    setExpandedSections((prev) => ({
      ...prev,
      ...Object.fromEntries(
        sections.map((s) => [s.sectionKey, prev[s.sectionKey] ?? true])
      ),
    }));
  }, [menuSections]);

  // Item Searchはページ内で独自のモバイル固定ヘッダーを持つため、Sidebar側のモバイルヘッダー/余白を無効化する
  const isItemSearchPage = location.pathname.includes("/item-search");
  const isSalesHistoryPage = location.pathname.includes("/sales-history");
  const isOrderInquiryPage = location.pathname.includes("/order-inquiry");
  const isItemListPage = location.pathname.includes("/item-list");
  const isBookingEntryPage = location.pathname.includes("/booking-entry");
  const isBookingHistoryPage = location.pathname.includes("/booking-history");
  const isNewItemApplicationPage = location.pathname.includes("/new-item-application");
  const isEcOrderOutputPage = location.pathname.includes("/ec-order-output");
  const isLineMessagePage = location.pathname.includes("/line-message");
  const isTaskBoardPage = location.pathname.includes("/task-board");
  const isConsignmentSalesAnalysisPage = location.pathname.includes("/consignment-sales-analysis");
  const isFullHeightScrollPage =
    isItemSearchPage || isOrderInquiryPage || isItemListPage || isBookingEntryPage || isBookingHistoryPage || isEcOrderOutputPage || isLineMessagePage || isTaskBoardPage || isConsignmentSalesAnalysisPage;

  // ユーザーの操作を検知してセッションを延長
  useEffect(() => {
    const handleActivity = () => {
      extendSession();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [extendSession]);

  // メニューの外側をクリックした時に閉じる
  const handleOutsideClick = (e) => {
    if (e.target.closest('aside') === null && 
        !e.target.closest('button.menu-trigger') && 
        !e.target.closest('.user-menu-container') && 
        !e.target.closest('.theme-toggle')) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  // ItemSearchからのサイドバー開閉イベントをリッスン
  useEffect(() => {
    const handleToggleSidebar = () => {
      setIsOpen(prev => !prev);
    };
    window.addEventListener('toggleSidebar', handleToggleSidebar);
    return () => {
      window.removeEventListener('toggleSidebar', handleToggleSidebar);
    };
  }, []);

  // 画面遷移時にメニューを閉じる
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // ユーザーメニューの外側をクリックした時に閉じる
  const handleUserMenuOutsideClick = (e) => {
    if (!e.target.closest('.user-menu-container')) {
      setShowUserMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleUserMenuOutsideClick);
    return () => {
      document.removeEventListener('click', handleUserMenuOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handleMyCustomerFilterUpdated = (event) => {
      if (typeof event?.detail?.active === 'boolean') {
        setIsMyCustomerActive(event.detail.active);
      }
    };

    window.addEventListener('myCustomerFilterUpdated', handleMyCustomerFilterUpdated);
    return () => {
      window.removeEventListener('myCustomerFilterUpdated', handleMyCustomerFilterUpdated);
    };
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const enabled =
      path.includes('/delivery-status') ||
      path.includes('/issue-tracker') ||
      path.includes('/order-inquiry') ||
      path.includes('/event-attendee-status');
    if (!enabled) {
      setIsMyCustomerActive(false);
    }
  }, [location.pathname]);

  const handleNavigate = async (path, functionId) => {
    // 操作履歴登録（メニューから起動した場合のみ function_id あり）
    if (functionId != null) {
      const token = localStorage.getItem("token");
      try {
        await fetch(`${API_BASE_URL}/operation-history`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ function_id: functionId }),
        });
      } catch ( error ) {
        console.error("Error posting operation history:", error);
        // 履歴登録失敗は画面遷移を妨げない
      }
    }
    // ItemSearchページの場合、一覧に戻るためのイベントを発行
    if (path === '/item-search') {
      window.dispatchEvent(new CustomEvent('resetItemSearch'));
    }
    navigate(`/dashboard${normalizeFunctionPath(path) || path}`);
    setIsOpen(false);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setIsOpen(false);
    
    // localStorageを完全にクリア
    localStorage.clear();
    
    // セッション終了
    endSession();
    
    // 強制的にログインページにリダイレクト
    window.location.href = '/login';
  };

  // セクションの開閉を切り替える関数（動的メニューのセクションキーで開閉）
  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };



  // 現在のパスに対応する m_function（メニュー項目）を取得
  const findMenuItemForPath = (pathname) => {
    const relative = (pathname || "").replace(/^\/dashboard\/?/, "/") || "/";
    const normalized = normalizeFunctionPath(relative);
    if (!normalized || normalized === "/") return null;

    const items = [];
    for (const block of menuSections) {
      if (block.type === "standalone") {
        items.push(block.item);
      } else {
        items.push(...(block.items || []));
      }
    }

    const sorted = items
      .filter((item) => item.path)
      .sort((a, b) => (normalizeFunctionPath(b.path) || "").length - (normalizeFunctionPath(a.path) || "").length);

    for (const item of sorted) {
      const itemPath = normalizeFunctionPath(item.path);
      if (!itemPath) continue;
      if (normalized === itemPath || normalized.startsWith(`${itemPath}/`)) {
        return item;
      }
    }
    return null;
  };

  // 現在のパスに基づいて m_function のアイコンとタイトルを表示
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes("/announcement-admin")) {
      return <span className="font-bold text-gray-800">Announcement Management</span>;
    }
    if (path.includes("/announcements")) {
      return <span className="font-bold text-gray-800">Announcements</span>;
    }
    if (path.includes("/line-message")) {
      return <span className="font-bold text-gray-800">LINE Message</span>;
    }
    if (path.includes("/consignment-sales-analysis")) {
      return <span className="font-bold text-gray-800">Consignment Sales Analysis</span>;
    }

    const item = findMenuItemForPath(location.pathname);
    if (!item) {
      return "KOBE-YA System";
    }

    const ItemIcon = item.icon_id ? getMenuIconComponent(item.icon_id) : null;
    const titleStyle = getColorStyle(item.label_color);
    const iconStyle = getColorStyle(item.icon_color);

    if (ItemIcon) {
      return (
        <div className="flex items-center">
          <span className="flex-shrink-0 w-5 h-5 mr-2" style={iconStyle}>
            <ItemIcon className="w-5 h-5" />
          </span>
          <span
            className={`font-bold${item.label_color ? "" : " text-gray-800"}`}
            style={titleStyle}
          >
            {item.menu_group_name}
          </span>
        </div>
      );
    }

    return (
      <span
        className={`font-bold${item.label_color ? "" : " text-gray-800"}`}
        style={titleStyle}
      >
        {item.menu_group_name}
      </span>
    );
  };

  // 検索機能が有効なページかどうかを判定
  const isMyCustomerEnabled = () => {
    const path = location.pathname;
    // 対象画面でのみサイドバーの MyCustomer 絞り込みを有効化
    return path.includes('/delivery-status') || path.includes('/issue-tracker') || path.includes('/order-entry') || path.includes('/order-inquiry') || path.includes('/event-attendee-status');
  };
  // 検索機能が有効なページかどうかを判定
  const isSearchEnabled = () => {
    const path = location.pathname;
    // delivery-status と consignment-delivery ページで検索機能を有効化
    return path.includes('/delivery-status') || path.includes('/consignment-delivery')||
     path.includes('/consignment-customer-status')|| path.includes('/fda-invoice-photo')|| path.includes('/issue-tracker')|| path.includes('/order-entry')|| path.includes('/sales-history') || path.includes('/consignment-status') || path.includes('/order-inquiry') || path.includes('/booking-history') || path.includes('/booking-entry') || path.includes('/consignment-sales-analysis'); 
  };

  // リロード機能が有効なページかどうかを判定
  const isReloadEnabled = () => {
    const path = location.pathname;
    // delivery-qr ページでリロード機能を有効化
    return path.includes('/delivery-qr');
  };

  const UserMenuContent = () => (
    <div className="relative user-menu-container">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowUserMenu(!showUserMenu);
        }}
        className="flex items-center space-x-2 text-gray-800 hover:text-gray-600 transition-colors"
      >
        <span>User : {user?.userName || 'Unknown'}</span>
      </button>
      {showUserMenu && (
        <div className="absolute top-full right-0 mt-1 w-32 bg-white rounded-lg shadow-lg py-1 z-50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <div className="p-1 rounded">
              <LogOut className="w-4 h-4" />
            </div>
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );



  return (
    <div className="flex h-full bg-gray-100 overflow-y-hidden">
      {/* メインコンテンツ */}
      <main className="flex h-full min-h-0 w-full flex-col overflow-hidden md:ml-0">
        {/* サイドバー */}
        <aside
          className={`fixed inset-y-0 left-0 w-[280px] bg-white text-gray-800 p-0 shadow-lg transform transition-transform duration-300 ease-in-out z-[70] flex flex-col
            ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
          onMouseLeave={() => {
            // モバイルではマウスリーブで閉じない
            if (window.innerWidth >= 768) {
              setIsOpen(false);
            }
          }}
        >
          {/* PC時のヘッダー */}
          <div className="mb-0 flex-shrink-0">
            <h2 className="w-full text-xl font-semibold text-white text-left bg-blue-500 pl-3">System Menu</h2>
          </div>

          {/* ユーザー情報 （モバイル・タブレット版のみ表示） */}
          <div className="mb-0 px-4 py-2 bg-gray-200 font-bold flex-shrink-0 lg:hidden">
            <div className="flex items-center gap-2">
              {isNewItemApplicationPage && (
                <button
                  type="button"
                  className="p-2 rounded-full hover:bg-gray-300 transition-colors"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("reloadNewItemApplication"));
                    setIsOpen(false);
                  }}
                  title="Reload"
                >
                  <SlReload className="w-5 h-5 text-gray-700" />
                </button>
              )}
              <button
                className="p-2 rounded-full hover:bg-gray-300 transition-colors"
                onClick={() => { navigate('/dashboard'); setIsOpen(false); }}
                title="Dashboard"
              >
                <MdHome className="w-6 h-6 text-gray-700" />
              </button>
              <div className="bg-transparent">
                <UserMenuContent />
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto">
            <ul className="space-y-0.5">
              {menuLoadError && (
                <li className="px-3 py-2 text-sm text-red-600">{menuLoadError}</li>
              )}
              {menuSections.map((block, blockIdx) => {
                if (block.type === "standalone") {
                  const item = block.item;
                  const ItemIcon = item.icon_id
                    ? getMenuIconComponent(item.icon_id)
                    : null;
                  return (
                    <li key={`standalone-${blockIdx}-${item.path}`}>
                      <button
                        type="button"
                        className="w-full flex items-center px-3 py-1.5 border-t border-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 text-left"
                        onClick={() => handleNavigate(item.path, item.function_id)}
                      >
                        {ItemIcon && (
                          <span className="flex-shrink-0 w-5 h-5 mr-2" style={getColorStyle(item.icon_color)}>
                            <ItemIcon className="w-5 h-5" />
                          </span>
                        )}
                        <span
                          className={`font-bold flex-1${item.label_color ? "" : " text-gray-800"}`}
                          style={getColorStyle(item.label_color)}
                        >
                          {item.menu_group_name}
                        </span>
                      </button>
                    </li>
                  );
                }

                const section = block;
                const isExpanded = expandedSections[section.sectionKey] !== false;
                const SectionIcon = section.sectionIconId
                  ? getMenuIconComponent(section.sectionIconId)
                  : null;
                return (
                  <li key={section.sectionKey}>
                    {/* セクション項目（path が NULL のメニュー。アコーディオンで開閉） */}
                    <button
                      type="button"
                      className="w-full flex items-center px-3 py-1.5 border-t border-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 text-left"
                      onClick={() => toggleSection(section.sectionKey)}
                    >
                      {SectionIcon && (
                        <span className="flex-shrink-0 w-5 h-5 mr-2" style={getColorStyle(section.sectionIconColor)}>
                          <SectionIcon className="w-5 h-5" />
                        </span>
                      )}
                      <span
                        className={`font-bold flex-1${section.sectionLabelColor ? "" : " text-gray-800"}`}
                        style={getColorStyle(section.sectionLabelColor)}
                      >
                        {section.sectionName}
                      </span>
                      <ChevronRight
                        className={`w-4 h-4 flex-shrink-0 transition-transform ${
                          isExpanded ? "rotate-90" : ""
                        }`}
                      />
                    </button>
                    {isExpanded && (
                      <ul>
                        {section.items.map((item, idx) => {
                          const ItemIcon = item.icon_id
                            ? getMenuIconComponent(item.icon_id)
                            : null;
                          return (
                            <li key={`${section.sectionKey}-${idx}`} className="ml-3">
                              <MenuItem
                                onClick={() => handleNavigate(item.path, item.function_id)}
                              >
                                {ItemIcon && (
                                  <span className="flex-shrink-0 w-5 h-5 mr-2" style={getColorStyle(item.icon_color)}>
                                    <ItemIcon className="w-5 h-5" />
                                  </span>
                                )}
                                <span
                                  className={`font-bold${item.label_color ? "" : " text-gray-600"}`}
                                  style={getColorStyle(item.label_color)}
                                >
                                  {item.menu_group_name}
                                </span>
                              </MenuItem>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>
        {/* モバイルヘッダー */}
        {/* モバイルヘッダー（Item Searchはページ内で独自ヘッダーを表示するため非表示） */}
        {!isFullHeightScrollPage && (
          <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white shadow-sm z-40">
            <div className="flex items-center justify-between px-4 h-full">
              <div className="flex items-center">
                <button
                  className="menu-trigger p-2 rounded-full hover:bg-gray-100 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                  >
                  <Menu className="w-6 h-6 text-gray-700" />
                  </button>
                  <h1 className="text-xl font-semibold ml-4 text-gray-800">{getPageTitle()}</h1>
              </div>
              <div className="flex items-center gap-2">
                {isMyCustomerEnabled() && (
                  <button
                    onClick={() => {
                      const nextState = !isMyCustomerActive;
                      setIsMyCustomerActive(nextState);
                      window.dispatchEvent(new CustomEvent('toggleMyCustomer', {
                        detail: { active: nextState }
                      }));
                    }}
                    className={`p-2 rounded-sm transition-colors ${isMyCustomerActive ? 'hover:bg-yellow-100' : 'hover:bg-gray-100'}`}
                  >
                    {isMyCustomerActive ? (
                      <FaStar className="w-5 h-5 text-yellow-500" />
                    ) : (
                      <FaRegStar className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                )}
                <AnnouncementBell />
                {isReloadEnabled() && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('reloadDeliveryDetails'))}
                    className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
                  >
                    <SlReload className="w-5 h-5  text-gray-900" />
                  </button>
                )}
                {isSalesHistoryPage && (
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('toggleSalesHistoryPageFind'))}
                    className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
                    aria-label="Find in page"
                    title="Find in page"
                  >
                    <TextSearch className="w-5 h-5 text-gray-500" />
                  </button>
                )}
                {isSearchEnabled() && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('toggleSearch'))}
                    className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
                  >
                    <FaSearch className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
            </div>
            </header>
        )}                                
        {/* PC用ヘッダー */}
        <header className="hidden lg:flex h-12 shrink-0 border-b border-gray-100 bg-white shadow-sm items-center pl-0 pr-4">
          <div className="flex-1 flex items-center gap-2">
            <button
              className="menu-trigger p-2 bg-blue-400 hover:bg-blue-500 rounded-sm transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              onMouseEnter={() => setIsOpen(true)}
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800 m-0">{getPageTitle()}</h1>
          </div>
          <div className="flex items-center gap-4">
            {isNewItemApplicationPage && (
              <button
                type="button"
                className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("reloadNewItemApplication"))
                }
                title="Reload"
              >
                <SlReload className="w-5 h-5 text-gray-500" />
              </button>
            )}
            {isMyCustomerEnabled() && (
              <button
                onClick={() => {
                  const nextState = !isMyCustomerActive;
                  setIsMyCustomerActive(nextState);
                  window.dispatchEvent(new CustomEvent('toggleMyCustomer', {
                    detail: { active: nextState }
                  }));
                }}
                className={`p-2 rounded-sm transition-colors ${isMyCustomerActive ? 'hover:bg-yellow-100' : 'hover:bg-gray-100'}`}
              >
                {isMyCustomerActive ? (
                  <FaStar className="w-5 h-5 text-yellow-500" />
                ) : (
                  <FaRegStar className="w-5 h-5 text-gray-500" />
                )}
              </button>
            )}
            <AnnouncementBell />
            <button
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              onClick={() => navigate('/dashboard')}
              title="Dashboard"
            >
              <MdHome className="w-6 h-6 text-gray-700" />
            </button>
            <div className="bg-transparent">
              <UserMenuContent />
            </div>
            {isReloadEnabled() && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('reloadDeliveryDetails'))}
                className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <SlReload className="w-5 h-5 text-gray-500" />
              </button>
            )}
            {isSearchEnabled() && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('toggleSearch'))}
                className="p-2 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <FaSearch className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
        </header>

        {/* オーバーレイ */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[55] md:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* PC用メニュートリガー（md〜lgサイズ時はホバーで開くサイドバー用のトリガーを表示） */}
        <div 
          className="hidden md:flex lg:hidden fixed top-1 left-0 z-0 items-center gap-0"
          onMouseEnter={() => setIsOpen(true)}
        >
          <button
            className="menu-trigger p-2 min-h-full bg-blue-400 hover:bg-blue-500 rounded-sm transition-colors"
          >
            <Menu className="w-6 h-6 text-white" />
          </button>
        </div>


        {/* コンテンツエリア */}
        <div 
          className={`flex-1 min-h-0 w-full ${isFullHeightScrollPage ? 'pt-0' : 'pt-14'} md:pt-0`}
          style={isFullHeightScrollPage ? { paddingTop: '0 !important', marginTop: '0 !important' } : {}}
        > 
          <div className={`w-full h-full min-h-0 max-w-none mx-auto ${isFullHeightScrollPage ? 'bg-white' : 'bg-white rounded-lg shadow-sm'} 
          p-0 md:p-0 ${isFullHeightScrollPage ? 'overflow-hidden' : 'overflow-auto'} ${isFullHeightScrollPage ? 'mt-0' : ''}`} 
          style={isFullHeightScrollPage ? { marginTop: '0 !important', paddingTop: '0 !important' } : {}}>
            {children}
          </div>
        </div> 
      </main>
    </div>
  );
}
